import { alpha, createTheme, Theme } from '@mui/material/styles';
import './topbar';

/**
 * Futuristic theme family — glass panels on a deep background, a faint neon
 * grid, gradient buttons, glowing chips and focus rings, Orbitron titles and
 * line-only (outline) icons. Fonts are self-hosted in styles/futuristic.css
 * (imported from fonts.ts); the browser fetches them only when one of these
 * themes uses them.
 *
 * Every variant comes from {@link createFuturisticTheme}, so they share one
 * look and differ only in colours. Dark variant names end in "Dark" — the
 * charts read light/dark mode from the name (chartThemeUtils).
 *
 * The page shell paints its own backgrounds inline / in makeStyles
 * (#authorizedLayout, #main, the AppBar, the side Drawer), so those are
 * restyled from CssBaseline with `body`-prefixed selectors: one extra type
 * selector out-specifies a component's single makeStyles class regardless of
 * stylesheet order. `.fidava-icon-tile` (side menu) and `.fidava-hub-icon`
 * (hub cards) are stable hooks the components carry in every theme; only
 * these themes style them.
 */

export interface IFuturisticColors {
    mode: 'light' | 'dark';
    /** Main accent (links, focus, icon lines). */
    accent: string;
    /** Second accent — the other end of every gradient. */
    accent2: string;
    /** Page background. */
    bg: string;
    /** Solid surface (menus, popovers, tooltips). */
    paper: string;
    text: string;
    textDim: string;
    /** Text on the accent gradient (contained buttons). */
    onAccent: string;
}

const glow = (c: string, a = 0.3, r = 12) => `0 0 ${r}px ${alpha(c, a)}`;

const TITLE_FONT = `'Orbitron', 'Exo 2', "Montserrat", sans-serif`;
const BODY_FONT = `'Exo 2', "Montserrat", "Helvetica", "Arial", sans-serif`;

export function createFuturisticTheme(c: IFuturisticColors): Theme {
    const dark = c.mode === 'dark';
    const gradient = `linear-gradient(90deg, ${c.accent}, ${c.accent2})`;
    // Glass panels: translucent surface + blur; light mode uses frosted white.
    const glass = dark ? alpha(c.paper, 0.72) : alpha('#FFFFFF', 0.72);
    const line = alpha(c.accent, dark ? 0.28 : 0.35);
    const faint = alpha(c.accent, dark ? 0.12 : 0.16);

    const titleVariant = { fontFamily: TITLE_FONT, letterSpacing: '0.06em', textTransform: 'uppercase' as const };
    // Smaller headings (card titles) stay in Exo 2: Orbitron is very wide and
    // overflows narrow cards / phones.
    const subTitleVariant = { fontFamily: BODY_FONT, fontWeight: 600, letterSpacing: '0.02em' };

    return createTheme({
        // The top bar is repainted as glass below (body .MuiAppBar-root), not
        // primary.light — its text colour is the theme text (see topbar.ts).
        fidavaTopbar: { text: c.text },
        spacing: 4,
        palette: {
            mode: c.mode,
            primary: { main: c.accent, contrastText: c.onAccent },
            secondary: { main: c.accent2 },
            warning: { main: '#FFB547' },
            error: { main: dark ? '#FF4D6D' : '#E11D48' },
            success: { main: dark ? '#2EF2A0' : '#059669' },
            info: { main: c.accent },
            background: { default: c.bg, paper: c.paper },
            text: { primary: c.text, secondary: c.textDim },
            divider: faint,
        },
        shape: { borderRadius: 14 },
        typography: {
            fontFamily: BODY_FONT,
            h1: titleVariant,
            h2: titleVariant,
            h3: titleVariant,
            h4: titleVariant,
            h5: subTitleVariant,
            h6: subTitleVariant,
            overline: { fontFamily: TITLE_FONT, letterSpacing: '0.12em' },
            button: { fontWeight: 600, letterSpacing: '0.06em' },
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        background: `radial-gradient(1200px 600px at 80% -10%, ${alpha(c.accent2, dark ? 0.18 : 0.12)}, transparent 60%),
                                     radial-gradient(900px 500px at -10% 110%, ${alpha(c.accent, dark ? 0.14 : 0.10)}, transparent 60%), ${c.bg}`,
                        backgroundAttachment: 'fixed',
                    },
                    // Faint neon grid. z-index -1 keeps it behind the page
                    // content (never over text).
                    'body::before': {
                        content: '""',
                        position: 'fixed',
                        inset: 0,
                        pointerEvents: 'none',
                        zIndex: -1,
                        backgroundImage: `linear-gradient(${alpha(c.accent, 0.05)} 1px, transparent 1px),
                                          linear-gradient(90deg, ${alpha(c.accent, 0.05)} 1px, transparent 1px)`,
                        backgroundSize: '40px 40px',
                        maskImage: 'linear-gradient(to bottom, black, transparent 85%)',
                        WebkitMaskImage: 'linear-gradient(to bottom, black, transparent 85%)',
                    },
                    // Let the gradient + grid show through the page shell.
                    'body #authorizedLayout, body #main': { backgroundColor: 'transparent !important' },

                    // ── Line-only icons ────────────────────────────────────
                    // FontAwesome glyphs are solid; drawing their outline
                    // instead of the fill gives neon line icons everywhere
                    // without swapping the icon set. Stroke width is in the
                    // glyph's 512-unit grid (≈ 1px at 18px).
                    'svg.svg-inline--fa': {
                        overflow: 'visible',
                        filter: `drop-shadow(${glow(c.accent, dark ? 0.55 : 0.35, 3)})`,
                    },
                    'svg.svg-inline--fa path': {
                        fill: 'transparent',
                        stroke: 'currentColor',
                        strokeWidth: 30,
                        strokeLinejoin: 'round',
                        strokeLinecap: 'round',
                    },

                    // ── Icon tiles (side menu, hub cards, top bar) ─────────
                    'body .fidava-icon-tile': {
                        background: `linear-gradient(145deg, ${alpha(c.accent, 0.16)}, ${alpha(c.accent2, 0.10)})`,
                        border: `1px solid ${alpha(c.accent, 0.5)}`,
                        boxShadow: `${glow(c.accent, 0.35, 10)}, inset 0 0 8px ${alpha(c.accent, 0.18)}`,
                        color: c.accent,
                    },
                    'body .Mui-selected .fidava-icon-tile, body .fidava-icon-tile:hover': {
                        borderColor: c.accent,
                        boxShadow: `${glow(c.accent, 0.6, 16)}, inset 0 0 10px ${alpha(c.accent2, 0.3)}`,
                    },
                    'body .fidava-hub-icon': {
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 96,
                        height: 96,
                        borderRadius: 22,
                        background: `linear-gradient(145deg, ${alpha(c.accent, 0.14)}, ${alpha(c.accent2, 0.08)})`,
                        border: `1px solid ${alpha(c.accent, 0.45)}`,
                        boxShadow: `${glow(c.accent, 0.3, 14)}, inset 0 0 12px ${alpha(c.accent, 0.15)}`,
                        transition: 'box-shadow .25s, border-color .25s',
                    },
                    'body .fidava-hub-icon svg': { color: c.accent, fontSize: 48 },
                    'body .MuiPaper-root:hover .fidava-hub-icon': {
                        borderColor: c.accent,
                        boxShadow: `${glow(c.accent, 0.55, 22)}, inset 0 0 14px ${alpha(c.accent2, 0.25)}`,
                    },
                    'body .MuiAppBar-root .MuiIconButton-root': {
                        borderRadius: 10,
                        border: `1px solid ${alpha(c.accent, 0.35)}`,
                        background: alpha(c.accent, 0.06),
                        color: c.accent,
                        margin: '0 2px',
                        transition: 'box-shadow .2s, border-color .2s',
                        '&:hover': { borderColor: c.accent, boxShadow: glow(c.accent, 0.5, 12), background: alpha(c.accent, 0.12) },
                    },
                    'body .MuiAppBar-root .MuiIconButton-root svg': { color: c.accent },
                    // Section you are in (UserMenu activeButtonProps): filled,
                    // full-strength border and a steady glow, so it stands out
                    // from the other (identically coloured) bar buttons.
                    'body .MuiAppBar-root .MuiIconButton-root.fidava-topbar-active': {
                        borderColor: c.accent,
                        background: alpha(c.accent, 0.24),
                        boxShadow: glow(c.accent, 0.55, 14),
                    },

                    // ── Shell: top bar and side drawer ─────────────────────
                    'body .MuiAppBar-root': {
                        background: dark
                            ? `linear-gradient(90deg, ${alpha(c.bg, 0.9)}, ${alpha(c.paper, 0.9)})`
                            : `linear-gradient(90deg, ${alpha('#FFFFFF', 0.85)}, ${alpha(c.bg, 0.85)})`,
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        borderBottom: `1px solid ${line}`,
                        boxShadow: glow(c.accent, 0.15, 24),
                        color: c.text,
                    },
                    'body .MuiDrawer-paper': {
                        background: dark ? alpha(c.bg, 0.92) : alpha('#FFFFFF', 0.86),
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        borderRight: `1px solid ${line}`,
                    },
                    // No blanket "reduced motion → transition: none" here: on any OS with
                    // animations off it also killed MUI's own slides (the form drawer
                    // opened without sliding in) — unlike every other theme. This
                    // theme's transitions are short glow/colour fades, not motion.
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: { backgroundImage: 'none' },
                    outlined: {
                        background: glass,
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        borderColor: line,
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        background: glass,
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: `1px solid ${line}`,
                        boxShadow: dark
                            ? `inset 0 0 20px ${alpha(c.accent, 0.05)}, 0 0 18px rgba(0,0,0,0.4)`
                            : `inset 0 0 20px ${alpha(c.accent, 0.04)}, 0 4px 18px ${alpha(c.accent2, 0.10)}`,
                        transition: 'box-shadow .25s, border-color .25s',
                        '&:hover': { borderColor: c.accent, boxShadow: glow(c.accent, 0.25, 22) },
                    },
                },
            },
            MuiTableContainer: {
                styleOverrides: { root: { background: glass, border: `1px solid ${line}`, borderRadius: 14 } },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: { borderColor: faint },
                    head: {
                        fontFamily: TITLE_FONT,
                        fontSize: '0.7rem',
                        color: c.accent,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                    },
                },
            },
            MuiTableRow: {
                styleOverrides: { root: { '&:hover': { background: alpha(c.accent, 0.06) } } },
            },
            MuiChip: {
                styleOverrides: {
                    root: ({ ownerState, theme }) => {
                        const key = ownerState.color && ownerState.color !== 'default' ? ownerState.color : 'primary';
                        const color = (theme.palette as any)[key]?.main ?? c.accent;
                        // Filled vs outlined must stay distinguishable: filter
                        // chips mark the selected one by switching to filled.
                        if (ownerState.variant === 'outlined') {
                            return {
                                background: 'transparent',
                                border: `1px solid ${alpha(color, 0.5)}`,
                                color,
                            };
                        }
                        return {
                            background: alpha(color, dark ? 0.3 : 0.2),
                            border: `1px solid ${color}`,
                            color,
                            fontWeight: 600,
                            boxShadow: glow(color, 0.5, 10),
                        };
                    },
                },
            },
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: { borderRadius: 12, textTransform: 'none' },
                    contained: {
                        background: gradient,
                        color: c.onAccent,
                        boxShadow: glow(c.accent2, 0.4, 16),
                        '&:hover': { background: gradient, boxShadow: glow(c.accent, 0.55, 20) },
                        '&.Mui-disabled': { background: alpha(c.textDim, 0.25), boxShadow: 'none' },
                    },
                    outlined: {
                        borderColor: line,
                        '&:hover': { borderColor: c.accent, boxShadow: glow(c.accent, 0.25) },
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    notchedOutline: { borderColor: line },
                    root: {
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: c.accent,
                            boxShadow: glow(c.accent, 0.3),
                        },
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: { background: c.paper, border: `1px solid ${line}`, color: c.text },
                },
            },
        },
    });
}

/** The family, keyed by theme name (see ThemePicker). */
export const futuristicThemes: Record<string, Theme> = {
    // Cyan + violet — the reference design.
    futuristicNeonDark: createFuturisticTheme({
        mode: 'dark', accent: '#00E5FF', accent2: '#B44CFF',
        bg: '#05070F', paper: '#0B1020', text: '#D6F6FF', textDim: '#7D93B8', onAccent: '#05070F',
    }),
    // Magenta + sunset orange.
    futuristicSynthwaveDark: createFuturisticTheme({
        mode: 'dark', accent: '#FF2E97', accent2: '#FF9F1C',
        bg: '#0D0518', paper: '#170A28', text: '#FFE3F2', textDim: '#B08AB8', onAccent: '#0D0518',
    }),
    // Terminal green.
    futuristicMatrixDark: createFuturisticTheme({
        mode: 'dark', accent: '#00FF9C', accent2: '#7CFF4F',
        bg: '#020A06', paper: '#06140D', text: '#D2FFE9', textDim: '#6FA48A', onAccent: '#020A06',
    }),
    // Teal + indigo.
    futuristicAuroraDark: createFuturisticTheme({
        mode: 'dark', accent: '#2DE2C4', accent2: '#6C7BFF',
        bg: '#04101A', paper: '#0A1A28', text: '#DDF9F4', textDim: '#7E9DB2', onAccent: '#04101A',
    }),
    // Frosted light glass for daytime; deeper accents keep text contrast.
    futuristicHologram: createFuturisticTheme({
        mode: 'light', accent: '#0077C8', accent2: '#7C3AED',
        bg: '#F2F7FC', paper: '#FFFFFF', text: '#0B1A33', textDim: '#4C6185', onAccent: '#FFFFFF',
    }),
};
